import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet } from 'lucide-react';
import { getTypeColor } from './constants';

// Map common ISO-4217 currency codes to flag emoji. Falls back to a plain code
// badge when the code does not map cleanly (e.g. XAU = gold, XOF = West African
// CFA across multiple countries).
const CURRENCY_FLAGS = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', JPY: '🇯🇵', CAD: '🇨🇦', AUD: '🇦🇺',
  CHF: '🇨🇭', CNY: '🇨🇳', INR: '🇮🇳', BRL: '🇧🇷', ZAR: '🇿🇦', NGN: '🇳🇬',
  KES: '🇰🇪', GHS: '🇬🇭', SGD: '🇸🇬', HKD: '🇭🇰', KRW: '🇰🇷', MXN: '🇲🇽',
  AED: '🇦🇪', SAR: '🇸🇦', EGP: '🇪🇬', NZD: '🇳🇿', SEK: '🇸🇪', NOK: '🇳🇴',
  DKK: '🇩🇰', PLN: '🇵🇱', TRY: '🇹🇷', RUB: '🇷🇺', THB: '🇹🇭', IDR: '🇮🇩',
  MYR: '🇲🇾', PHP: '🇵🇭', VND: '🇻🇳', TWD: '🇹🇼', ILS: '🇮🇱', ARS: '🇦🇷',
  CLP: '🇨🇱', COP: '🇨🇴', PEN: '🇵🇪', UYU: '🇺🇾',
};

/**
 * Phase 1 multi-currency view — Quick Win (no FX feed yet).
 *
 * Groups the user's investments by their native currency and displays the raw
 * native-currency totals. NO conversion is performed — each row stays in its own
 * currency. Once the FX feed is wired (Phase 2), we'll add a converted home-
 * currency total alongside each row.
 */
const HoldingsByCurrencyCard = ({ investments }) => {
  // Group ONLY direct + indirect investments by currency — to stay consistent
  // with the headline "Total Investments" card which also excludes Related
  // Assets (memorandum-only items aren't part of net worth).
  //
  // Defensive: legacy rows can have owner_type = null/missing/blank. We treat
  // anything that isn't explicitly 'indirect' or 'related' as direct, matching
  // the backend's _separate_by_ownership fallback.
  const { byCurrency, relatedByCurrency } = useMemo(() => {
    const counted = {};
    const related = {};
    (investments || []).forEach((inv) => {
      const code = (inv.currency || 'UNK').toUpperCase();
      const value = inv.current_value || inv.amount || 0;
      const bucket = inv.owner_type === 'related' ? related : counted;
      if (!bucket[code]) bucket[code] = { code, total: 0, count: 0 };
      bucket[code].total += value;
      bucket[code].count += 1;
    });
    const sortFn = (a, b) => (b.count !== a.count ? b.count - a.count : a.code.localeCompare(b.code));
    return {
      byCurrency: Object.values(counted).sort(sortFn),
      relatedByCurrency: Object.values(related).sort(sortFn),
    };
  }, [investments]);

  if (byCurrency.length === 0 && relatedByCurrency.length === 0) return null;

  const fmt = (code, amount) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      // Unknown / non-ISO code (e.g. UNK) — fall back to a plain number.
      return `${code} ${Math.round(amount).toLocaleString('en-US')}`;
    }
  };

  return (
    <Card className="portfolio-card" data-testid="holdings-by-currency-card">
      <CardHeader>
        <CardTitle className="text-base sm:text-xl font-serif flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-600" />
          Total Investments by Currency
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          What you actually hold in each currency — no conversion applied.
        </p>
      </CardHeader>
      <CardContent>
        {byCurrency.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {byCurrency.map((row, index) => (
              <div
                key={row.code}
                className={`${getTypeColor(index)} text-white rounded-lg shadow-md p-3 sm:p-4`}
                data-testid={`currency-row-${row.code}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl leading-none" aria-hidden="true">
                    {CURRENCY_FLAGS[row.code] || ''}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide">
                    {row.code}
                  </span>
                </div>
                <div className="text-lg sm:text-2xl font-mono font-bold mb-1">
                  <span data-sensitive="monetary">{fmt(row.code, row.total)}</span>
                </div>
                <p className="text-white/90 text-xs sm:text-sm">
                  {row.count} investment{row.count !== 1 ? 's' : ''}
                </p>
              </div>
            ))}
          </div>
        )}

        {relatedByCurrency.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="related-currency-section">
            <h4 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
              Related Assets (memorandum only)
            </h4>
            <p className="text-[11px] text-slate-500 mb-3">
              Trusts and related holdings tracked for your records — not included in your Total Investments.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {relatedByCurrency.map((row) => (
                <div
                  key={`related-${row.code}`}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4"
                  data-testid={`related-currency-row-${row.code}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl leading-none" aria-hidden="true">
                      {CURRENCY_FLAGS[row.code] || ''}
                    </span>
                    <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-slate-600">
                      {row.code}
                    </span>
                  </div>
                  <div className="text-lg sm:text-2xl font-mono font-bold mb-1 text-slate-900">
                    <span data-sensitive="monetary">{fmt(row.code, row.total)}</span>
                  </div>
                  <p className="text-slate-500 text-xs sm:text-sm">
                    {row.count} asset{row.count !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HoldingsByCurrencyCard;
